import {
  IonButton,
  IonButtons,
  IonContent,
  IonFooter,
  IonHeader,
  IonIcon,
  IonSelect,
  IonSelectOption,
  IonTitle,
  IonToolbar,
} from '@ionic/react';
import { chevronBack, chevronForward, close } from 'ionicons/icons';
import {
  Dispatch,
  ReactElement,
  SetStateAction,
  useCallback,
  useRef,
} from 'react';
import { useTranslation } from 'react-i18next';
import { BaseButton } from '../../../../../../../../components/button/base-button';
import { FullScreenIonModal } from '../../../../../../../../components/modal/full-screen-ion-modal';
import { MultipassScheduleProps } from '../multipass-schedule';
import { MultipassScheduleIndividualView } from './multipass-schedule-individual-view';

interface MultipassScheduleIndividualViewModalProps
  extends MultipassScheduleProps {
  isOpen: boolean;
  selectedPassIndex: number;
  setSelectedPass: Dispatch<SetStateAction<number | undefined>>;
  onDidDismiss: () => void;
  isExecuting?: boolean;
}

const MultipassScheduleIndividualViewModal = (
  props: MultipassScheduleIndividualViewModalProps
): ReactElement => {
  const {
    selectedPassIndex,
    numberOfPasses,
    forceFullScreenModals,
    isOpen,
    onDidDismiss,
    setSelectedPass,
    isExecuting,
  } = props;

  const { i18n } = useTranslation();

  const modalRef = useRef<HTMLIonModalElement>(null);
  const currentPassDisplay = (selectedPassIndex ?? 0) + 2;
  const individualTitle = i18n.t('common:pass_of_passes', {
    pass: currentPassDisplay,
    passes: numberOfPasses + 1,
  });

  return (
    <FullScreenIonModal
      ref={modalRef}
      isOpen={isOpen}
      presentingElement={props.presentingEl}
      onDidDismiss={() => {
        onDidDismiss();
      }}
      isFullScreen={forceFullScreenModals}
      className="multipass-schedule-modal"
    >
      <IonHeader>
        <IonToolbar>
          <IonButtons slot={'start'}>
            <IonButton
              onClick={() => {
                modalRef.current?.dismiss();
              }}
            >
              <IonIcon icon={close} size="large" />
            </IonButton>
          </IonButtons>
          <IonTitle>{individualTitle}</IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent>
        <MultipassScheduleIndividualView
          currentPass={selectedPassIndex ?? 0}
          disabled={props.disabled}
          presentingEl={modalRef.current ?? undefined}
          forceFullScreenModals={forceFullScreenModals}
        />
      </IonContent>
      <IonFooter>
        <IonToolbar>
          <MultipassScheduleIndividualToolbarContent
            numberOfPasses={numberOfPasses}
            selectedPassIndex={selectedPassIndex}
            setSelectedPass={setSelectedPass}
            onDone={() => {
              modalRef.current?.dismiss();
            }}
            isExecuting={isExecuting}
          />
        </IonToolbar>
      </IonFooter>
    </FullScreenIonModal>
  );
};

interface MultipassScheduleIndividualViewToolbarContentProps {
  numberOfPasses: number;
  selectedPassIndex: number;
  setSelectedPass: Dispatch<SetStateAction<number | undefined>>;
  onDone: () => void;
  isExecuting?: boolean;
}

const MultipassScheduleIndividualToolbarContent = (
  props: MultipassScheduleIndividualViewToolbarContentProps
): ReactElement => {
  const {
    numberOfPasses,
    selectedPassIndex,
    setSelectedPass,
    onDone,
    isExecuting,
  } = props;

  const { i18n } = useTranslation();

  const navigateLeftOrRight = useCallback(
    (direction: 'left' | 'right') => {
      setSelectedPass((prev) => {
        if (prev === undefined) {
          return;
        }

        if (direction === 'left') {
          return prev <= 0 ? prev : prev - 1;
        } else {
          return prev >= numberOfPasses - 1 ? prev : prev + 1;
        }
      });
    },
    [numberOfPasses, setSelectedPass]
  );

  const passLocation = `${selectedPassIndex + 2} / ${numberOfPasses + 1}`;

  // Disabled states
  const isPassSelectorDisabled = isExecuting;
  const isPreviousButtonDisabled = selectedPassIndex === 0 || isExecuting;
  const isNextButtonDisabled =
    selectedPassIndex === numberOfPasses - 1 || isExecuting;

  return (
    <div className="toolbar-content-container">
      {numberOfPasses > 1 && (
        <>
          <IonSelect
            value={selectedPassIndex}
            onIonChange={(e) => setSelectedPass(e.detail.value)}
            interface="popover"
            aria-label={i18n.t('common:select_pass')}
            selectedText={i18n.t('common:go_to')}
            className="jump-to"
            disabled={isPassSelectorDisabled}
          >
            {Array.from({ length: numberOfPasses }, (_, i) => (
              <IonSelectOption key={i} value={i}>
                {i18n.t('common:pass_with_number', {
                  number: i + 2,
                })}
              </IonSelectOption>
            ))}
          </IonSelect>
          <IonButtons className="nav-button-container">
            <BaseButton
              buttonProps={{
                disabled: isPreviousButtonDisabled,
                onClick: () => {
                  navigateLeftOrRight('left');
                },
              }}
            >
              <IonIcon icon={chevronBack} slot="icon-only" />
            </BaseButton>
            {passLocation}
            <BaseButton
              buttonProps={{
                disabled: isNextButtonDisabled,
                onClick: () => {
                  navigateLeftOrRight('right');
                },
              }}
            >
              <IonIcon icon={chevronForward} slot="icon-only" />
            </BaseButton>
          </IonButtons>
        </>
      )}
      <div className="done-btn-container">
        <BaseButton
          buttonProps={{
            onClick: () => {
              onDone();
            },
          }}
        >
          {i18n.t('common:done')}
        </BaseButton>
      </div>
    </div>
  );
};

export {
  MultipassScheduleIndividualViewModal,
  MultipassScheduleIndividualToolbarContent,
};
